/**
 * Service layer — Drawing Sheet PDF Inserts domain
 *
 * Owns: Drawing Sheet PDF Inserts (tblkyglTh8edVI63w — Airtable, pending migration)
 *       PDF Insert Types          (Supabase public.pdf_insert_types — migrated from tblO7k7kMVucPVtF5)
 *
 * Business rules enforced here (not in components):
 *  - At most one insert per Drawing Sheet
 *  - Inserts are never deleted
 *  - Insert types are cached for the session
 */

import { apiGet, apiPost, apiPatch, apiPatchBatch } from './_api.js'
// r20c: the shared singleton, NOT a second createClient — two GoTrue clients in one
// tab share the same auth storage key and warn about undefined behavior (surfaced
// by the Modeler's Realtime work; lib/supabase.js is the one sanctioned client)
import { supabase as supa } from '../lib/supabase.js'
import { getSelectedConceptId, noteSelectedConcept } from './design.js'
import { getProjectSheets } from './sheets.js'

const TBL_INSERT = 'tblkyglTh8edVI63w'

let _typesCache = null

/**
 * Fetch all PDF Insert Types from Supabase (cached for the session).
 * Returns [{id, name, generationLink}]
 */
export async function getPdfInsertTypes() {
  if (_typesCache) return _typesCache
  const { data, error } = await supa
    .from('pdf_insert_types')
    .select('id, name, generation_link')
    .order('name')
  if (error) throw new Error('getPdfInsertTypes: ' + error.message)
  _typesCache = data.map(r => ({
    id:             r.id,
    name:           r.name,
    generationLink: r.generation_link || '',
  }))
  return _typesCache
}

/**
 * Fetch the PDF Insert record for a Drawing Sheet (max 1 per sheet).
 * Returns the raw Airtable record or null when none exists.
 * @param {string} sheetId — Drawing Sheet record ID
 */
export async function getSheetPdfInsert(sheetId) {
  const formula = 'FIND("' + sheetId + '",ARRAYJOIN({Record ID (from Drawing Sheet)},","))'
  const records = await apiGet(
    TBL_INSERT,
    'filterByFormula=' + encodeURIComponent(formula) + '&maxRecords=1'
  )
  return records.length ? records[0] : null
}

/**
 * Create a new PDF Insert record for a Drawing Sheet.
 * Callers are responsible for confirming no insert exists before calling.
 * @param {string} sheetId      — Drawing Sheet record ID
 * @param {string} insertTypeId — PDF Insert Type record ID (Airtable ID, bridge key)
 * @returns {Promise<object>} The created Airtable record
 */
export async function createSheetPdfInsert(sheetId, insertTypeId, projectId = null) {
  const fields = {
    'Drawing Sheet': [sheetId],
    'Insert Type':   [insertTypeId],
  }
  // Project Concept is what every project lookup on the insert (project, building type,
  // FHSZ, city/state, permits…) hangs off, so the generated PDF is empty without it.
  const conceptId = await getSelectedConceptId(projectId).catch(() => null)
  if (conceptId) fields['Project Concept'] = [conceptId]
  return apiPost(TBL_INSERT, fields)
}

/**
 * The project's active concept changed: re-point EVERY PDF Insert on the project's drawing
 * sheets at it, so the next Generate prints the new concept's data. Already-generated PDFs
 * are not regenerated — they stay as issued until someone clicks Generate again.
 * Best-effort; returns { updated, failed }.
 */
export async function relinkProjectPdfInserts(projectId, conceptId) {
  if (!projectId || !conceptId) return { updated: 0, failed: 0 }
  noteSelectedConcept(projectId, conceptId)
  const sheets = await getProjectSheets(projectId)
  const sheetIds = (sheets || []).map(s => s.id)
  const inserts = []
  for (let i = 0; i < sheetIds.length; i += 20) {
    const chunk = sheetIds.slice(i, i + 20)
    const formula = 'OR(' + chunk.map(id => 'FIND("' + id + '",ARRAYJOIN({Record ID (from Drawing Sheet)},","))').join(',') + ')'
    const recs = await apiGet(TBL_INSERT, 'filterByFormula=' + encodeURIComponent(formula) + '&maxRecords=100')
    inserts.push(...(recs || []))
  }
  const stale = inserts.filter(r => {
    const cur = (r.fields && r.fields['Project Concept']) || []
    return !(cur.length === 1 && cur[0] === conceptId)
  })
  if (!stale.length) return { updated: 0, failed: 0 }
  const res = await apiPatchBatch(TBL_INSERT, stale.map(r => ({ id: r.id, fields: { 'Project Concept': [conceptId] } })))
  return { updated: res.succeeded.length, failed: res.failed.length }
}

/**
 * Point an existing insert at the project's Selected Concept when it doesn't already.
 * Returns the concept id now linked, or null when there's nothing to link (no decidable
 * concept) — the record is left untouched in that case. Callers read the returned id
 * to update their local copy.
 */
export async function syncPdfInsertConcept(insert, projectId) {
  if (!insert || !insert.id) return null
  const conceptId = await getSelectedConceptId(projectId)
  if (!conceptId) return null
  const current = (insert.fields && insert.fields['Project Concept']) || []
  if (current.length === 1 && current[0] === conceptId) return conceptId
  await apiPatch(TBL_INSERT, insert.id, { 'Project Concept': [conceptId] })
  return conceptId
}

/**
 * Update fields on an existing PDF Insert record.
 * @param {string} insertId — PDF Insert record ID
 * @param {object} fields   — Airtable field name → new value
 */
export async function updateSheetPdfInsert(insertId, fields) {
  return apiPatch(TBL_INSERT, insertId, fields)
}
